var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var AutoTestStepResult_1;
import { Maybe, Nothing } from 'purify-ts';
import * as api from '@codegrade/api';
import { assert, immutable } from '@codegrade/utils';
import * as utils from '@/utils';
import { CacheDecorator } from '@/utils/cache';
const FINISHED_STATES = new Set([
    'passed',
    'partial',
    'failed',
    'timed_out',
    'hidden',
    'skipped',
]);
const PARTIAL_STEP_TYPES = new Set([
    'custom_output',
    'junit_test',
    'code_quality',
]);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let CustomOutputLog = class CustomOutputLog extends api.CustomOutputLog {
};
CustomOutputLog = __decorate([
    immutable.immutable
], CustomOutputLog);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let JunitTestLog = class JunitTestLog extends api.JunitTestLog {
};
JunitTestLog = __decorate([
    immutable.immutable
], JunitTestLog);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let QualityTestLog = class QualityTestLog extends api.QualityTestLog {
};
QualityTestLog = __decorate([
    immutable.immutable
], QualityTestLog);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let RunProgramLog = class RunProgramLog extends api.RunProgramLog {
};
RunProgramLog = __decorate([
    immutable.immutable
], RunProgramLog);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let IOTestStepLog = class IOTestStepLog extends api.IOTestStepLog {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(stepResult, data) {
        super(data);
        this.stepResult = stepResult;
        const state = data.state;
        Object.defineProperty(this, 'state', {
            get: () => this._getState(state),
        });
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    _getState(state) {
        switch (state) {
            case 'passed':
            case 'failed':
            case 'timed_out':
            case 'skipped':
                return state;
            case 'not_started':
            case 'running':
                if (this.stepResult.state === 'skipped') {
                    return 'skipped';
                }
                return state;
            default:
                return assert.AssertionError.assertNever(state);
        }
    }
};
IOTestStepLog = __decorate([
    immutable.immutable
], IOTestStepLog);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let IOTestLog = class IOTestLog extends api.IOTestLog {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(stepResult, data) {
        super(data);
        this.steps = data.steps.map(step => new IOTestStepLog(stepResult, step));
    }
};
IOTestLog = __decorate([
    immutable.immutable
], IOTestLog);
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
let AutoTestStepLogBase = class AutoTestStepLogBase extends api.AutoTestStepLogBase {
};
AutoTestStepLogBase = __decorate([
    immutable.immutable
], AutoTestStepLogBase);
let AutoTestStepResult = AutoTestStepResult_1 = class AutoTestStepResult {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    constructor(
    // The id is only used to retrieve the attachment of a
    // step. This should probably not be done that way, but that
    // requires a API change.
    id, autoTestStep, achievedPoints, log, startedAt, attachmentId, suite, _state, stepIndex) {
        this.id = id;
        this.autoTestStep = autoTestStep;
        this.achievedPoints = achievedPoints;
        this.startedAt = startedAt;
        this.attachmentId = attachmentId;
        this.suite = suite;
        this._state = _state;
        this.stepIndex = stepIndex;
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        this._cache = new CacheDecorator();
        this.log = log.map(l => {
            if (l instanceof api.IOTestLog) {
                return new IOTestLog(this, l);
            }
            else if (l instanceof api.RunProgramLog) {
                return new RunProgramLog(l);
            }
            else if (l instanceof api.CustomOutputLog) {
                return new CustomOutputLog(l);
            }
            else if (l instanceof api.JunitTestLog) {
                return new JunitTestLog(l);
            }
            else if (l instanceof api.QualityTestLog) {
                return new QualityTestLog(l);
            }
            else if (l instanceof api.AutoTestStepLogBase) {
                return new AutoTestStepLogBase(l);
            }
            else {
                return assert.AssertionError.assertNever(l);
            }
        });
        Object.freeze(this);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static makeNonStarted(step, stepIndex, suite) {
        return new AutoTestStepResult_1(Maybe.empty(), step, 0, Maybe.empty(), Maybe.empty(), Maybe.empty(), suite, 'not_started', stepIndex);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    static fromServerData(data, step, stepIndex, suite) {
        return new AutoTestStepResult_1(Maybe.of(data.id), step, data.achievedPoints, data.log, data.startedAt, data.attachmentId, suite, data.state, stepIndex);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get stepId() {
        return this.autoTestStep.id.unsafeCoerce();
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get _result() {
        return this.suite.set.result;
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get _previousStep() {
        if (this.stepIndex > 0) {
            return Maybe.of(this.suite.stepResults[this.stepIndex - 1]);
        }
        return Nothing;
    }
    get prevCheckPointFailed() {
        // Both no previous set or previous set is still running should be
        // handled the same here, both should not count as a failed checkpoint.
        const previousSetFailed = this.suite.set.previousSetPassed
            .join()
            .mapOrDefault(utils.not, false);
        if (previousSetFailed) {
            return true;
        }
        return this._previousStep.mapOrDefault(prevStep => (prevStep.autoTestStep.type === 'check_points' && prevStep.state === 'failed') ||
            prevStep.prevCheckPointFailed, false);
    }
    get state() {
        switch (this._state) {
            case 'not_started':
                // Hidden tests are only run when the result is a final
                // result. So if the state is not_started (this is also the
                // state if we don't have result yet) set the state to skipped.
                if (this.autoTestStep.hidden &&
                    this._result.isFinal.mapOrDefault(utils.not, false)) {
                    return 'skipped';
                }
            // fallthrough
            case 'running':
                if (this._result.failed || this.prevCheckPointFailed) {
                    switch (this._state) {
                        case 'not_started':
                            return 'skipped';
                        case 'running':
                            return 'failed';
                        default:
                            return assert.AssertionError.assertNever(this._state);
                    }
                }
                // If the entire result is skipped, mark all the steps
                // as skipped too.
                if (this._result.state === 'skipped') {
                    return 'skipped';
                }
                return this._state;
            case 'passed':
                // For steps where one can get a "partial" score, we update
                // the state here based on the amount of points achieved.
                // We must check, however, that fewer than the maximum points
                // were achieved, because otherwise steps with 0 weight might
                // be marked failed when they actually passed.
                if (PARTIAL_STEP_TYPES.has(this.autoTestStep.type)) {
                    const points = this.achievedPoints;
                    if (points < this.autoTestStep.weight) {
                        if (points === 0) {
                            return 'failed';
                        }
                        else {
                            return 'partial';
                        }
                    }
                }
                return this._state;
            case 'failed':
            case 'timed_out':
            case 'skipped':
                return this._state;
            default:
                return assert.AssertionError.assertNever(this._state);
        }
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get finished() {
        return FINISHED_STATES.has(this.state);
    }
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    get weight() {
        if (this.state === 'hidden') {
            return 0;
        }
        return this.autoTestStep.weight;
    }
};
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestStepResult.prototype, "prevCheckPointFailed", null);
__decorate([
    CacheDecorator.cache
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestStepResult.prototype, "state", null);
AutoTestStepResult = AutoTestStepResult_1 = __decorate([
    immutable.immutable
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
], AutoTestStepResult);
export { AutoTestStepResult };
